import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_BASE ?? "";

async function getJSON(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function SeatManagement() {
  const { showtime_id } = useParams();
  const navigate = useNavigate();

  const [seats, setSeats] = useState([]);
  const [availability, setAvailability] = useState({});
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [bookedBy, setBookedBy] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showtime, setShowtime] = useState(null);
  const [predefinedTickets, setPredefinedTickets] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem("seatSelectionData");
    if (stored) {
      const data = JSON.parse(stored);
      setPredefinedTickets(data.order || []);
    }
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      // 1) showtime
      const showData = await getJSON(`/api/showtimes/id/${showtime_id}`);
      if (!Array.isArray(showData) || showData.length === 0) throw new Error("Showtime not found");
      const show = showData[0];

      // 2) movie details (best effort)
      try {
        const movieData = await getJSON(`/api/movies/${show.MOVIE_ID}`);
        show.poster = movieData?.POSTER_URL || "";
        show.title = movieData?.MOVIE_NAME || show.movie_title;
      } catch {
        show.poster = "";
        show.title = show.movie_title;
      }

      // 3) seats for hall
      const seatList = await getJSON(`/api/seats/${show.HALL_ID}`);
      setSeats(Array.isArray(seatList) ? seatList : []);

      // 4) availability for showtime (init if missing)
      let avail = await getJSON(`/api/seats-reserve/seat_availability/${showtime_id}`);
      if (!Array.isArray(avail) || avail.length === 0) {
        await getJSON(`/api/seats-reserve/seat_availability/init`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ showtime_id }),
        });
        avail = await getJSON(`/api/seats-reserve/seat_availability/${showtime_id}`);
      }
      setAvailability(Object.fromEntries(avail.map(a => [a.SEAT_ID, a])));

      setShowtime(show);
    } catch (e) {
      console.error("❌ SeatManagement load error:", e);
      setError(e.message || "Load failed");
    } finally {
      setLoading(false);
      setSelectedSeats([]); // reset selection after any reload
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showtime_id]);

  const maxToSelect = predefinedTickets.reduce((sum, t) => sum + (t.quantity || 0), 0);

  const toggleSeat = (seatId) => {
    const status = availability[seatId]?.STATUS;
    if (!status || status === "sold") return;
    setSelectedSeats(prev => {
      if (prev.includes(seatId)) return prev.filter(id => id !== seatId);
      if (prev.length >= maxToSelect) return prev;
      return [...prev, seatId];
    });
  };

  const reserveSeats = async () => {
    if (!bookedBy || selectedSeats.length === 0) return;
    await getJSON(`/api/seats-reserve/seat_availability/reserve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ showtime_id, seat_ids: selectedSeats, booked_by: bookedBy }),
    });
    await loadAll();
  };

  const markSold = async () => {
    if (selectedSeats.length === 0) return;
    for (const seatId of selectedSeats) {
      // mark one by one (keep your API shape)
      // If your API supports bulk, you can batch this.
      await getJSON(`/api/seats-reserve/seat_availability/mark-sold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ showtime_id, seat_id: seatId }),
      });
    }
    await loadAll();
  };

  const cancelReservation = async () => {
    if (selectedSeats.length === 0) return;
    for (const seatId of selectedSeats) {
      await getJSON(`/api/seats-reserve/seat_availability/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ showtime_id, seat_id: seatId }),
      });
    }
    await loadAll();
  };

  // group seats by row (handles lowercase/UPPERCASE props)
  const getRowGroups = () => {
    const grouped = {};
    seats.forEach(s => {
      const row = s.seat_row ?? s.SEAT_ROW;
      if (!row) return;
      if (!grouped[row]) grouped[row] = [];
      grouped[row].push({
        ...s,
        seat_row: s.seat_row ?? s.SEAT_ROW,
        seat_column: s.seat_column ?? s.SEAT_COLUMN,
        row_label: s.row_label ?? s.ROW_LABEL,
        is_label: s.is_label ?? s.IS_LABEL,
        label_text: s.label_text ?? s.LABEL_TEXT,
        SEAT_ID: s.SEAT_ID ?? s.seat_id, // normalize id fallback
      });
    });
    return grouped;
  };

  const getStatusCounts = () => {
    let reserved = 0, sold = 0, available = 0;
    Object.values(availability).forEach(seat => {
      if (seat.STATUS === "reserved") reserved++;
      else if (seat.STATUS === "sold") sold++;
      else available++;
    });
    return { reserved, sold, available, total: reserved + sold + available };
  };

  const stats = getStatusCounts();

  if (loading) {
    return <div className="p-6 text-center text-gray-600 font-semibold">Loading seat management…</div>;
  }
  if (error) {
    return <div className="p-6 text-center text-red-600 font-bold">Error: {error}</div>;
  }
  if (!showtime) {
    return <div className="p-6 text-center text-gray-600">Showtime not found.</div>;
  }

  return (
    <div className="flex gap-10 p-6 max-w-6xl mx-auto">
      <div className="w-1/3">
        <img
          src={showtime.poster || "https://via.placeholder.com/300x450?text=No+Poster"}
          alt={showtime.title}
          className="rounded-xl shadow mb-4 w-full object-cover"
        />
        <h2 className="text-xl font-bold mb-1">{showtime.title}</h2>
        <p className="text-sm text-gray-700">
          🗓 Date: {showtime.show_date} / 🕐 Time: {showtime.show_time}
        </p>
        <p className="text-sm text-gray-700">🏟 Hall: {showtime.hall_name}</p>
        <div className="mt-4 text-sm bg-gray-100 p-3 rounded shadow-inner">
          <p>🎫 Reserved: <strong>{stats.reserved}</strong></p>
          <p>💰 Sold: <strong>{stats.sold}</strong></p>
          <p>✅ Available: <strong>{stats.available}</strong></p>
          <p>🎟 Total seats: <strong>{stats.total}</strong></p>
        </div>
      </div>

      <div className="w-2/3">
        <h1 className="text-2xl font-semibold mb-4 text-center text-gray-700">🪑 Seat Management</h1>

        {Object.entries(getRowGroups()).map(([rowNum, rowSeats]) => {
          const firstSeat = rowSeats[0];
          const rowLabel = firstSeat?.row_label || rowNum;
          const maxColumn = Math.max(...rowSeats.map(s => s.seat_column || 0));

          const seatsByCol = {};
          rowSeats.forEach(seat => {
            const col = seat.seat_column;
            if (!seat.is_label && col) {
              seatsByCol[col] = { ...seat, ...availability[seat.SEAT_ID] };
            }
          });

          return (
            <div key={rowNum} className="flex items-center justify-center mb-2">
              <div className="w-20 pl-1 text-left font-semibold text-sm text-gray-800">{rowLabel}</div>

              <div className="grid" style={{ gridTemplateColumns: `repeat(${maxColumn}, 3rem)` }}>
                {Array.from({ length: maxColumn }, (_, idx) => {
                  const column = idx + 1;
                  const seat = seatsByCol[column];
                  if (!seat) return <div key={`empty-${idx}`} className="w-10 h-10 mx-1 my-1" />;

                  const seatId = seat.SEAT_ID || seat.seat_id;
                  const status = availability[seatId]?.STATUS || "available";
                  const isSelected = selectedSeats.includes(seatId);

                  return (
                    <button
                      key={seatId}
                      onClick={() => toggleSeat(seatId)}
                      className={`w-10 h-10 mx-1 my-1 rounded text-sm font-semibold text-center leading-tight flex items-center justify-center
                        ${status === "sold" ? "bg-red-600 cursor-not-allowed text-white" :
                          status === "reserved" ? "bg-yellow-500 text-white" :
                          isSelected ? "bg-green-600 text-white" : "bg-gray-200 hover:bg-green-300"}`}
                      disabled={status === "sold"}
                      title={status}
                    >
                      {seat.label_text || seat.SEAT_NUMBER || seat.seat_column}
                    </button>
                  );
                })}
              </div>

              <div className="w-20 pl-5 text-left font-semibold text-sm text-gray-800">{rowLabel}</div>
            </div>
          );
        })}

        <div className="mt-6">
          <input
            type="text"
            placeholder="👤 Booking Name"
            value={bookedBy}
            onChange={e => setBookedBy(e.target.value)}
            className="border px-4 py-2 rounded w-full mb-2"
          />
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={reserveSeats}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              disabled={!bookedBy || selectedSeats.length === 0}
            >
              ✅ Reserve {selectedSeats.length}
            </button>
            <button
              onClick={markSold}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              disabled={selectedSeats.length === 0}
            >
              💰 Sell
            </button>
            <button
              onClick={cancelReservation}
              className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
              disabled={selectedSeats.length === 0}
            >
              ❌ Cancel
            </button>
            <button
              onClick={() => window.print()}
              className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-900"
            >
              🖨️ Print Seat Plan
            </button>
            <button
              onClick={() => navigate("/admin/dashboard")}
              className="mb-4 text-blue-600 hover:underline print-hide"
            >
              🔙 Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
